define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"csi",
	"csi/_base/underscore",
	"csi/def",
	"csi/heading/_base",
	"csi/reflection",
	"csi/types",
	"csi/xml",
	"ProductSpec/SizeChart/CustomViewSizeChartOptions",
	"../SizeChart",
	"./Number"
], function(declare,lang,domClass,csi,_,def,csiHeading,reflection,types,xml,{SizeChartDimensionFormatType},SizeChart,NumberRen) {

	var	_htmlDiff = ' / <span class="diff">',
	_htmlEndSpan = '</span>',
	_htmlTolerance = '</span><span class="tolerance noexport">',

	_readFormat = lang.hitch(types, types.getNumberRead),
	_writeFormat = lang.hitch(types, types.getNumberWrite);

	return declare(NumberRen, {
		bBaseRenderer: true,

		// constants used to render heading
		html1: '<span>',

		// Boolean: indicate if vector should look for and render comparison values if available
		//bRenderComparisonValues: false,

		// String: css class to use for base size column (with leading space)
		//cssBaseSize: " base",

		headingToHtml: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			// summary: render size chart value for cell
			domClass.add(refNode, this._getDomClassString(ctx, h));

			var	v = this._getFormattedValue(ctx, h, null, _readFormat);
			if (v === null || (v == csi.EMPTY_CHAR && (this._getBaseIndexInRange(ctx) != ctx.keyValue))) {
				refNode.innerHTML = csi.EMPTY_CHAR;
			} else {
				var	s = this.html1 + v + (SizeChart.bPct ? _htmlTolerance + this._getToleranceValue(ctx, h) : "") + _htmlEndSpan;

				// add comparison value
				if (this.bRenderComparisonValues && (ctx.view.getDimensionDifference(ctx.getNodeURL(), ctx.keyValue) != null)) {
					ctx.bComparisonValues = true;
					s += _htmlDiff + this._getFormattedValue(ctx, h, null, _readFormat) + _htmlEndSpan;
					ctx.bComparisonValues = false;
				}

				refNode.innerHTML = s;
			}

			// setup cell for editing
			csiHeading.markHeadingNode(refNode, h, ctx);
			csiHeading.setHeadingValueKey(refNode, ctx.keyValue);

			if (this._canEditVectorNumber(ctx, h)) {
				csiHeading.markHeadingEditable(refNode, h);
			}
		},

		/**
		 * @override
		 * If comparing increments, then add tooltip actions to apply increments; otherwise hide them
		 */
		headingToTooltip: function(ctx, h, tooltip) {
			if (!this.bRenderComparisonValues || (ctx.view.getDimensionDifference(ctx.getNodeURL(), ctx.keyValue) != null)) {
				return this.inherited(arguments);
			}
		},

		toValueDynamic: function(/*Context*/ctx, /*Heading*/h, /*Number?*/v) {
			// summary: convert value to editable value based on format, if empty character replace it with null so it wont show up on text box
			var v = this._getFormattedValue(ctx, h, v, _writeFormat);
			return (v == csi.EMPTY_CHAR) ? "" : v;
		},

		_canEditVectorNumber: function(/*Context*/ctx, /*Heading*/h) {
			// summary: calculate edit permission once rather than once per cell
			return !ctx.bPrint && !ctx.isReadOnly() && xml.nodeHasAccess(ctx.getNode(), h.getRight(), h.m_id) && def.processRules(ctx, h.m_action?.m_rules);
		},

		_getBaseIndex: function(/*Context*/ctx, /*Node*/dim) {
			// summary: returns base size index for the SizeChartDimension.
			// 1-d dimension on 2-d size chart, use dim1 base index.
			var scRevData = ctx.view.getSizeChartRevisionData(dim);
			return scRevData.isTwoDim() && !ctx.view.displayTwoDimension(dim) ? scRevData.getDim1BaseIndex() : this._getBaseIndexInRange(ctx);
		},

		_getBaseIndexInRange: function(/*Context*/ctx) {
			// summary: return true if node and index in context represents base size
			var	dim = ctx.getNode();
			var	ranges = ctx.view.getSubSizeRangeData(dim);

			// 1-d dimension on 2-d size chart, use dim1 base index.
			var scRevData = ctx.view.getSizeChartRevisionData(dim);
			return scRevData.isTwoDim() && !ctx.view.displayTwoDimension(dim) ? scRevData.getDim1BaseIndex() : ranges.getBaseIndex(ctx.keyValue);
		},

		_getDomClassString: function(/*Context*/ctx, /*Heading*/h) {
			let classString = "attrNumber value";
			const dim = ctx.getNode();

			if (this._getBaseIndex(ctx, dim) == ctx.keyValue) {
				classString += " PatternBase";
			} else if (SizeChart.isNotInitialized(this._getValue(ctx, h)) && (h.m_id == "Increments" || h.m_id == "Pattern")) {
				const sizeChartRevision = ctx.getParent();
				const isDraft = (sizeChartRevision.State == reflection.REVISION_DRAFT);

				if (ctx.view.sizeChartHighlightBlank && !dim.Blanks && isDraft) {
					classString += " PatternBlank";
				}
			}

			return classString;
		},

		_getFormattedValue: function(/*Context*/ctx, /*Heading*/h, /*Number?*/value, /*Function*/func) {
			// summary: format value
			if (value == undefined) {
				value = this._getValue(ctx, h);

				// return empty string if blank values for dimension unless showing revised values
				if (ctx.getNode().Blanks && (this._getBaseIndexInRange(ctx) != ctx.keyValue)) {
					//return blank space to keep the container height while switching visibility
					return csi.EMPTY_CHAR;
				}
			}

			if (SizeChart.isNotInitialized(value)) {
				return csi.EMPTY_CHAR;
			}

			var	format;

			if (this._isFraction(ctx)) {
				format = types.FORMAT_IN_FRACTION;
			} else if(ctx.view.sizeChartDimensionFormat == SizeChartDimensionFormatType.DEFAULT) {
				format = (!this.isIncrementDisplay(ctx)) ? SizeChart.formatAbs : SizeChart.formatInc;
			} else {
				format = ctx.view.sizeChartDimensionFormat;
			}

			return func(this._applyFactor(ctx, value), format);
		},

		_getToleranceValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return string for absolute tolerance value
			var	dimValues = SizeChart.getDimensionValues(ctx, h.m_id, this.bRenderComparisonValues);
			var	value = dimValues.abs[ctx.keyValue] * dimValues.tol;
			var	format = this._isFraction(ctx) ? types.FORMAT_IN_FRACTION : SizeChart.formatTol;
			return this.formatValue(this._applyFactor(ctx, value), format);
		},

		_getValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: extract value for size chart - it will depend on absolute/increment display setting and base size index
			var	dim = ctx.getNode();
			var	dimValues = SizeChart.getDimensionValues(ctx, h.m_id, this.bRenderComparisonValues);
			var	baseIndex = this._getBaseIndex(ctx, dim);
			var	index = ctx.keyValue;

			if (baseIndex != index) {
				if (this.isIncrementDisplay(ctx)) {
					return dimValues.inc[index];
				}

				if (this.isRelativeDisplay(ctx)) {
					var absValue = dimValues.abs[index];
					return SizeChart.isNotInitialized(absValue) ? absValue : SizeChart.sumDoubleValues(absValue, -dimValues.abs[baseIndex]);
				}
			}

			return dimValues.abs[index];
		}
	});
});